/**
 * Replaces dynamically rendered buttons with anchor links.
 * @param {string} selector - CSS selector for the buttons to replace.
 * @param {string} url - Destination URL for the anchor.
*/
function replaceButtonsWithAnchors( selector, url ) {
    const customClasses = [
        'custom-button'
    ];

    const buttons = document.querySelectorAll( selector );

    for ( let i = 0; i < buttons.length; i++ ) {
        const button = buttons[i];
        const anchor = document.createElement( 'a' );

        anchor.href = url;
        anchor.className = button.className;
        anchor.style.cssText = button.style.cssText;
        anchor.innerHTML = button.innerHTML;
        anchor.classList.add( ...customClasses );

        button.replaceWith( anchor );
    }
}

/**
 * Initializes custom JavaScript functionality.
*/
function initializeCustomJS() {
    if ( window.location.pathname === '/pages/rewards' ) {
        const selector = '.yotpo-coupons-redemption-widget .yotpo-login-button .yotpo-login-link button';
        const url = '/customer_authentication/login?return_to=' + encodeURIComponent( '/pages/rewards' );

        replaceButtonsWithAnchors( selector, url );

        const observer = new MutationObserver(() => {
            replaceButtonsWithAnchors( selector, url );
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }
}


/**
 * Updates cart content without disconnecting the preserved checkout integration.
 * @param { HTMLElement } element - Existing cart section.
 * @param { string } html - Updated Shopify section HTML.
 * @param { boolean } [preserveElements=true] - Preserve checkout during nonempty updates.
*/
function updateCartSectionContents( element, html, preserveElements = true ) {
    const template = document.createElement( 'template' );
    template.innerHTML = html;

    if ( preserveElements === false ) {
        element.replaceChildren( template.content );
    } else {
        const selector = '[data-cart-preserve="checkout"]';
        const currentMatches = element.querySelectorAll( selector );
        const updatedMatches = template.content.querySelectorAll( selector );

        if ( currentMatches.length !== 1 || updatedMatches.length !== 1 ) {
            throw new Error( 'Expected exactly one checkout preservation wrapper in each cart section.' );
        }

        const currentPath = getCartPreservedPath( element, currentMatches[0] );
        const updatedPath = getCartPreservedPath( template.content, updatedMatches[0] );

        if ( currentPath.length !== updatedPath.length ) {
            throw new Error( 'Cart checkout ancestor structure has changed.' );
        }

        for ( let i = 1; i < currentPath.length; i++ ) {
            const currentNode = currentPath[i];
            const updatedNode = updatedPath[i];

            if ( currentNode.nodeType !== updatedNode.nodeType ) {
                throw new Error( 'Cart checkout ancestor structure does not match.' );
            }

            if ( currentNode.nodeType === Node.ELEMENT_NODE ) {
                if ( currentNode.tagName !== updatedNode.tagName || currentNode.id !== updatedNode.id ) {
                    throw new Error( 'Cart checkout ancestor elements do not match.' );
                }

                if ( i < currentPath.length - 1 && currentNode.className !== updatedNode.className ) {
                    throw new Error( 'Cart checkout ancestor classes do not match.' );
                }
            }
        }

        for ( let i = 0; i < currentPath.length - 1; i++ ) {
            updateCartSiblingRegions( currentPath[i], updatedPath[i], currentPath[i + 1], updatedPath[i + 1] );
        }
    }
}

/**
 * Gets the ancestor chain from a cart section to its checkout wrapper.
 * @param { Node } root - Cart section or parsed HTML fragment.
 * @param { Element } preserved - Checkout wrapper.
 * @returns { Node[] } Ancestor chain including the root and wrapper.
*/
function getCartPreservedPath( root, preserved ) {
    const path = [];
    let node = preserved;

    while ( node !== null ) {
        path.unshift( node );

        if ( node === root ) {
            break;
        }

        node = node.parentNode;
    }

    if ( path[0] !== root ) {
        throw new Error( 'Checkout wrapper is outside the cart section.' );
    }

    return path;
}

/**
 * Refreshes siblings on either side of a preserved descendant without moving it.
 * @param { Node } currentParent - Live parent containing the preserved descendant.
 * @param { Node } updatedParent - Parsed parent with new cart content.
 * @param { Node } currentAnchor - Live descendant that must remain connected.
 * @param { Node } updatedAnchor - Corresponding descendant in the parsed HTML.
*/
function updateCartSiblingRegions( currentParent, updatedParent, currentAnchor, updatedAnchor ) {
    let node = currentParent.firstChild;

    while ( node !== currentAnchor ) {
        const nextNode = node.nextSibling;
        currentParent.removeChild( node );
        node = nextNode;
    }

    node = updatedParent.firstChild;

    while ( node !== updatedAnchor ) {
        currentParent.insertBefore( node.cloneNode( true ), currentAnchor );
        node = node.nextSibling;
    }

    node = currentAnchor.nextSibling;

    while ( node !== null ) {
        const nextNode = node.nextSibling;
        currentParent.removeChild( node );
        node = nextNode;
    }

    node = updatedAnchor.nextSibling;

    while ( node !== null ) {
        currentParent.appendChild( node.cloneNode( true ) );
        node = node.nextSibling;
    }
}

if ( document.readyState === 'loading' ) {
    document.addEventListener( 'DOMContentLoaded', initializeCustomJS );
} else {
    initializeCustomJS();
}

/**
 * Refreshes storefront state when Chrome restores a page from bfcache.
 *
 * Returning from Shopify checkout through the browser's Back button
 * may restore the original Hero Arts page without requesting new HTML.
 * This prevents Shopify Liquid and normal page initialization from
 * executing again, potentially leaving cart-related content outdated.
 *
 * Instead of forcing a page reload, trigger the theme's existing cart
 * refresh functionality to synchronize the storefront with Shopify.
*/
window.addEventListener( 'pageshow', function onPageShow( event ) {
    if ( event.persisted === true && document.body.dataset.site === 'hero' ) {
        document.dispatchEvent( new CustomEvent( 'cart-drawer:refresh' ) );
    }
} );